import React from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import SingleFileUpload from 'src/components/SingleFileUpload';
import { UploadedFileSchema } from 'src/Ghg/CodeOfConductSurvey/types';
import { useLcaOrganization } from 'src/common/hooks';
import { useLcaImage, useLcaProduct } from 'src/Lca/hooks';
import { client } from 'src/utils/api-client';

// OrganizationUploadedImage accepts raster images only, so no SVG here.
const PASSPORT_QR_FILE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
// below this width an uploaded code tends to blur when printed on a label
const MIN_PASSPORT_QR_WIDTH = 600;

const normalizeUrl = (url: string) =>
  url.trim().replace(/\/+$/, '').toLowerCase();

interface PassportQrEditorProps {
  productId: number;
  passportToken: string;
  passportUrl: string;
}

/**
 * Swaps the passport data carrier from the passport editor itself. The same
 * fields live on the product form, but that is not where people look for them
 * once they are already arranging the passport.
 */
export default function PassportQrEditor({
  productId,
  passportToken,
  passportUrl,
}: PassportQrEditorProps) {
  const { t } = useTranslation('lca', {
    keyPrefix: 'product.sharePage.customQr',
  });
  const queryClient = useQueryClient();
  const organization = useLcaOrganization();
  const { product, isLoading } = useLcaProduct(productId);

  const savedImageId = product?.passport_qr_image_id ?? null;
  const savedTargetUrl = product?.passport_qr_target_url || '';

  const [imageId, setImageId] = React.useState<string | null>(null);
  const [targetUrl, setTargetUrl] = React.useState('');

  // Only the stored values are watched, never the query object: a background
  // refetch hands back a fresh object every time, and reacting to that would
  // wipe whatever the user is in the middle of typing.
  const isLoaded = !!product;
  React.useEffect(() => {
    if (!isLoaded) return;
    setImageId(savedImageId);
    setTargetUrl(savedTargetUrl);
  }, [isLoaded, savedImageId, savedTargetUrl]);

  const { image } = useLcaImage(imageId ?? '');
  const [isTooNarrow, setIsTooNarrow] = React.useState(false);

  const imageUrl = image?.url;
  React.useEffect(() => {
    if (!imageUrl) {
      setIsTooNarrow(false);
      return;
    }

    const probe = new Image();
    probe.onload = () =>
      setIsTooNarrow(probe.naturalWidth < MIN_PASSPORT_QR_WIDTH);
    probe.onerror = () => setIsTooNarrow(false);
    probe.src = imageUrl;
  }, [imageUrl]);

  const saveMutation = useMutation({
    mutationFn: () =>
      client.put(`/web/lca/products/${productId}/passport-qr`, {
        passport_qr_image_id: imageId,
        passport_qr_target_url: targetUrl.trim(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['lca', 'products', productId]);
      queryClient.invalidateQueries(['lca', 'passport', passportToken]);
      toast.success(t('toasts.saved') as string);
    },
    onError: () => {
      toast.error(t('toasts.saveFailed') as string);
    },
  });

  if (isLoading || !product) return null;

  const trimmedTargetUrl = targetUrl.trim();
  const hasTargetMismatch =
    !!trimmedTargetUrl &&
    normalizeUrl(trimmedTargetUrl) !== normalizeUrl(passportUrl);
  const isDirty =
    imageId !== savedImageId || trimmedTargetUrl !== savedTargetUrl;

  return (
    <>
      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" mb={2}>
        {t('title')}
      </Typography>

      <Stack spacing={2} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          {t('description')}
        </Typography>

        {image && (
          <Box
            component="img"
            src={image.url}
            alt={t('previewAlt')}
            sx={{ objectFit: 'contain', maxWidth: 180, maxHeight: 180 }}
          />
        )}

        <Box sx={{ width: '100%' }}>
          <SingleFileUpload
            url={`/web/organizations/${organization.id}/images`}
            attachButtonText={t('upload')}
            acceptedFileTypes={PASSPORT_QR_FILE_TYPES}
            onSuccess={(uploaded: UploadedFileSchema) =>
              setImageId(uploaded.id)
            }
            onRemoved={() => setImageId(null)}
            file={image ?? null}
          />
        </Box>

        {isTooNarrow && <Alert severity="warning">{t('lowResolution')}</Alert>}

        <TextField
          fullWidth
          label={t('targetUrlOptional')}
          value={targetUrl}
          onChange={(event) => setTargetUrl(event.target.value)}
        />

        {hasTargetMismatch && (
          <Alert severity="warning">{t('targetUrlMismatch')}</Alert>
        )}

        {organization.can_write_data && (
          <Button
            variant="primary"
            onClick={() => saveMutation.mutate()}
            disabled={!isDirty || saveMutation.isLoading}
          >
            {t('save')}
          </Button>
        )}
      </Stack>
    </>
  );
}
